import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Menu, X } from 'lucide-react';
import styles from './Navbar.module.css';
import Logo from './Logo';
import { ICON_SIZES, STROKE_WIDTHS } from '../constants/iconSizes';

export default function Navbar({ darkMode }) {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const navItems = [
    { id: 'home', label: t('nav.home') || 'Home' },
    { id: 'services', label: t('nav.services') || 'Services' },
    { id: 'areas', label: t('nav.areas') || 'Areas' },
    { id: 'why', label: t('nav.why') || 'Why Us' },
    { id: 'feedback', label: t('nav.feedback') || 'Feedback' },
    { id: 'faq', label: t('nav.faq') || 'FAQ' },
    { id: 'team', label: t('nav.team') || 'Team' },
    { id: 'contact', label: t('nav.contact') || 'Contact' }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsOpen(false);
    }
  };

  const navAriaLabel = i18n.language === 'ar' ? 'التنقل الرئيسي' : 'Primary navigation';

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      {/* Desktop Navbar */}
      <nav
        className={`${styles.nav} ${darkMode ? styles.dark : styles.light} ${styles.desktop}`}
        aria-label={navAriaLabel}
      >
        <a
          href="#home"
          onClick={(event) => {
            event.preventDefault();
            scrollToSection('home');
          }}
          className={styles.logoBtn}
          aria-label="سطحة هيدروليك الخبر"
        >
          <Logo
            size={24}
            variant="full"
            colorScheme={darkMode ? 'theme-dark' : 'theme-light'}
          />
        </a>
        {navItems.map((item) => (
          <a
            key={item.id}
            href={`#${item.id}`}
            onClick={(event) => {
              event.preventDefault();
              scrollToSection(item.id);
            }}
            className={styles.navBtn}
          >
            {item.label}
          </a>
        ))}
      </nav>

      {/* Mobile Navbar */}
      <div className={styles.mobileNav}>
        <button
          className={`${styles.hamburger} ${darkMode ? styles.dark : styles.light}`}
          onClick={toggleMenu}
          aria-label="Toggle navigation menu"
          aria-expanded={isOpen}
          aria-pressed={isOpen}
        >
          {isOpen ? <X size={ICON_SIZES.MENU} strokeWidth={STROKE_WIDTHS.BOLD} aria-hidden="true" /> : <Menu size={ICON_SIZES.MENU} strokeWidth={STROKE_WIDTHS.BOLD} aria-hidden="true" />}
        </button>

        {isOpen && (
          <div className={`${styles.dropdown} ${darkMode ? styles.dark : styles.light}`} role="navigation" aria-label="Mobile navigation menu">
            <div className={styles.dropdownLogo}>
              <Logo
                size={22}
                variant="full"
                colorScheme={darkMode ? 'theme-dark' : 'theme-light'}
              />
              <span>سطحة الخبر</span>
            </div>
            {navItems.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                onClick={(event) => {
                  event.preventDefault();
                  scrollToSection(item.id);
                }}
                className={styles.dropdownItem}
              >
                {item.label}
              </a>
            ))}
          </div>
        )}
      </div>

      {isOpen && (
        <div className={styles.overlay} onClick={() => setIsOpen(false)} />
      )}
    </>
  );
}

Navbar.propTypes = {
  darkMode: PropTypes.bool.isRequired,
};